import { Link } from "react-router-dom";
import { Mail, MapPin, Facebook, Instagram, Twitter, Youtube, Send } from "lucide-react";

const SOCIALS = [
    { name: "Instagram", icon: Instagram, href: "https://www.instagram.com/filescadapp", testId: "social-instagram" },
    { name: "Facebook", icon: Facebook, href: "https://www.facebook.com/profile.php?id=61555873696369&mibextid=kFxxJD", testId: "social-facebook" },
    { name: "YouTube", icon: Youtube, href: "https://www.youtube.com/@filescad", testId: "social-youtube" },
    { name: "X / Twitter", icon: Twitter, href: "https://twitter.com/filescad", testId: "social-twitter" },
    {
        name: "Pinterest",
        icon: (props) => (
            <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
            </svg>
        ),
        href: "https://in.pinterest.com/filescad",
        testId: "social-pinterest",
    },
    { name: "Telegram", icon: Send, href: "https://t.me/filescad", testId: "social-telegram" },
];

const PLAY_URL = "https://play.google.com/store/apps/details?id=com.filescad.app";

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-ink-950 text-slate-300 overflow-hidden"
        >
            <div className="absolute inset-0 bg-blueprint-dark opacity-60 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20">
                <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8">
                    <div className="col-span-2 md:col-span-4">
                        <div className="flex items-center gap-3 mb-6">
                            <img
                                src="https://play-lh.googleusercontent.com/zqPvmjZJnHrvIqHfnpWWhkAAQF4LiYtAor5yPuolfibCLmINaN6VXEa5xDVqD9JDjaUd0YoJ3pXz2tzKGZSObw=w240-h480-rw"
                                alt="FilesCAD logo"
                                className="w-12 h-12 rounded-2xl"
                            />
                            <div>
                                <div className="font-display font-black text-2xl text-white">FilesCAD</div>
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Exclusive CNC Designs</div>
                            </div>
                        </div>
                        <p className="text-slate-400 max-w-md leading-relaxed">
                            Established 2020. A library of ready-to-cut CNC design files — Jali patterns, doors, geometric and floral motifs — crafted for professionals.
                        </p>

                        <a
                            href={PLAY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-play-badge"
                            className="inline-block mt-8 hover:opacity-90 transition-opacity"
                        >
                            <img
                                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                alt="Get it on Google Play"
                                className="h-14"
                            />
                        </a>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="text-white font-display font-bold mb-5 text-sm uppercase tracking-[0.18em]">Company</h4>
                        <ul className="space-y-3">
                            <li>
                                <Link to="/about.html" data-testid="footer-about-link" className="text-slate-400 hover:text-white transition">
                                    About Us
                                </Link>
                            </li>
                            <li>
                                <Link to="/contact-us.html" data-testid="footer-contact-link" className="text-slate-400 hover:text-white transition">
                                    Contact Us
                                </Link>
                            </li>
                            <li>
                                <Link to="/faq.html" data-testid="footer-faq-link" className="text-slate-400 hover:text-white transition">
                                    FAQs
                                </Link>
                            </li>
                            <li>
                                <Link to="/download" data-testid="footer-download-link" className="text-slate-400 hover:text-white transition">
                                    Download by PIN
                                </Link>
                            </li>
                            <li>
                                <a
                                    href={PLAY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-play-link"
                                    className="text-slate-400 hover:text-white transition"
                                >
                                    Google Play Store
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <h4 className="text-white font-display font-bold mb-5 text-sm uppercase tracking-[0.18em]">Legal</h4>
                        <ul className="space-y-3">
                            <li>
                                <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-slate-400 hover:text-white transition">
                                    Privacy Policy
                                </Link>
                            </li>
                            <li>
                                <Link to="/terms-and-conditions" data-testid="footer-terms-link" className="text-slate-400 hover:text-white transition">
                                    Terms &amp; Conditions
                                </Link>
                            </li>
                            <li>
                                <Link to="/refund-policy" data-testid="footer-refund-link" className="text-slate-400 hover:text-white transition">
                                    Refund Policy
                                </Link>
                            </li>
                            <li>
                                <Link to="/license.html" data-testid="footer-license-link" className="text-slate-400 hover:text-white transition">
                                    License
                                </Link>
                            </li>
                            <li>
                                <Link to="/disclaimer.html" data-testid="footer-disclaimer-link" className="text-slate-400 hover:text-white transition">
                                    Disclaimer
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="col-span-2 md:col-span-3">
                        <h4 className="text-white font-display font-bold mb-5 text-sm uppercase tracking-[0.18em]">Contact</h4>
                        <ul className="space-y-4 text-slate-400">
                            <li className="flex items-start gap-3">
                                <Mail className="w-4 h-4 mt-1 text-blue-400 shrink-0" />
                                <a
                                    href="mailto:support@filescad.com"
                                    data-testid="footer-contact-email"
                                    className="hover:text-white transition text-sm break-all"
                                >
                                    support@filescad.com
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 mt-1 text-blue-400 shrink-0" />
                                <span className="text-sm leading-relaxed">
                                   Jodhpur, Rajasthan 342001<br />India
                                </span>
                            </li>
                        </ul>

                        <h4 className="text-white font-display font-bold mt-8 mb-5 text-sm uppercase tracking-[0.18em]">Follow us</h4>
                        <div className="flex flex-wrap gap-3" data-testid="footer-socials">
                            {SOCIALS.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <a
                                        key={s.name}
                                        href={s.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={s.name}
                                        data-testid={s.testId}
                                        className="w-10 h-10 grid place-items-center rounded-xl bg-white/5 ring-1 ring-white/10 text-slate-300 hover:bg-blue-600 hover:text-white hover:ring-blue-600 hover:-translate-y-0.5 transition-all"
                                    >
                                        <Icon className="w-4 h-4" />
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4">
                    <p className="text-xs text-slate-500">
                        © {new Date().getFullYear()} FilesCAD. All rights reserved. Developed by <a
                            href="https://manojsuthar.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-developed-link"
                            className="text-slate-400 hover:text-white underline"
                        >
                            Haptics Apps
                        </a>.
                    </p>
                    <p className="text-xs text-slate-500 font-mono">
                        DXF · CDR · DWG — Ready to cut.
                    </p>
                </div>
            </div>
        </footer>
    );
}
